import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Grid from '@mui/material/Unstable_Grid2';
import Framework from 'benchmarking-domain/framework';
import { PermissionsHelper } from 'benchmarking-domain/permissions-helper';
import Plant from 'benchmarking-domain/plant';
import Project from 'benchmarking-domain/project';
import _default from 'currency-codes-ts';
import { CurrencyCode } from 'currency-codes-ts/dist/types';
import { enqueueSnackbar } from 'notistack';

import useApi from '../../../hooks/use-api';
import { useOrganisation } from '../../../hooks/use-organisation';
import { useUser } from '../../../hooks/use-user';
import { UserSelect } from '../../core/shared/user-select';

interface ProjectFormProps {
  project?: Project;
  onSubmit: (data: Project) => Promise<void>;
}

export function ProjectForm({ project, onSubmit }: ProjectFormProps): React.JSX.Element {
  const { subscribeFrameworks, subscribePlants } = useApi();
  const { organisationId } = useOrganisation();
  const { user } = useUser();

  const [prevProject, setPrevProject] = useState<Project | undefined>(project);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [frameworks, setFrameworks] = useState<Framework[]>();
  const [frameworksLoadingTriggered, setFrameworksLoadingTriggered] = useState<boolean>(false);

  const [plants, setPlants] = useState<Plant[]>();
  const [plantsLoadingTriggered, setPlantsLoadingTriggered] = useState<boolean>(false);

  const [technologies, setTechnologies] = useState<string[]>();

  const [name, setName] = useState<string>(project?.name ?? '');
  const [plant, setPlant] = useState<string>(project?.plant ?? '');
  const [plantId, setPlantId] = useState<string>(project?.plantId ?? '');
  const [framework, setFramework] = useState<string>(project?.framework ?? '');
  const [frameworkId, setFrameworkId] = useState<string>(project?.frameworkId ?? '');
  const [technology, setTechnology] = useState<string>(project?.technology ?? '');
  const [managers, setManagers] = useState<string[]>(project?.managers ?? []);
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>(project?.currencyCode ?? 'USD');
  const [startMonth, setStartMonth] = useState<number>(project?.startMonth ?? 1);
  const [startYear, setStartYear] = useState<number>(project?.startYear ?? new Date().getFullYear());
  const [endMonth, setEndMonth] = useState<number>(project?.endMonth ?? 12);
  const [endYear, setEndYear] = useState<number>(project?.endYear ?? new Date().getFullYear());

  if (prevProject !== project) {
    setPrevProject(project);
    setName(project?.name ?? '');
    setPlant(project?.plant ?? '');
    setPlantId(project?.plantId ?? '');
    setFramework(project?.framework ?? '');
    setFrameworkId(project?.frameworkId ?? '');
    setTechnology(project?.technology ?? '');
    setManagers(project?.managers ?? []);
    setStartMonth(project?.startMonth ?? 1);
    setStartYear(project?.startYear ?? new Date().getFullYear());
    setEndMonth(project?.endMonth ?? 12);
    setEndYear(project?.endYear ?? new Date().getFullYear());
  }

  useEffect(() => {
    if (!frameworksLoadingTriggered) {
      return;
    }

    const unsubscribe = subscribeFrameworks(true, setFrameworks);

    return () => {
      unsubscribe();
    };
  }, [frameworksLoadingTriggered, subscribeFrameworks]);

  useEffect(() => {
    if (!organisationId || !plantsLoadingTriggered) {
      return;
    }

    const unsubscribe = subscribePlants(organisationId, setPlants);

    return () => {
      unsubscribe();
    };
  }, [plantsLoadingTriggered, subscribePlants, organisationId]);

  const isEditMode = (): boolean => {
    return project?.id !== undefined && project?.id !== '';
  };

  const handleFrameworkChange = (event: SelectChangeEvent) => {
    const selectedFramework = frameworks?.find((element) => element.id === event.target.value);
    const selectedPlant = plants?.find((element) => element.id === plantId);

    if (selectedFramework) {
      setFrameworkId(selectedFramework.id);
      setFramework(selectedFramework.name);
    } else {
      setFrameworkId('');
      setFramework('');
    }

    if (selectedFramework && selectedPlant) {
      const commonTechnologies = selectedFramework.technologies?.filter((frameworkTechs) =>
        selectedPlant.technologies?.some((plantTechs) => frameworkTechs === plantTechs)
      );
      setTechnologies(commonTechnologies);
    } else {
      setTechnologies(undefined);
    }
  };

  const handlePlantChange = (event: SelectChangeEvent) => {
    const selectedPlant = plants?.find((element) => element.id === event.target.value);
    const selectedFramework = frameworks?.find((element) => element.id === frameworkId);

    if (selectedPlant) {
      setPlantId(selectedPlant.id);
      setPlant(selectedPlant.name ?? '');

      let defaultCountryCode: CurrencyCode = 'USD';

      if (selectedPlant.country) {
        const currencyRecords = _default.country(selectedPlant.country);

        if (currencyRecords.length > 0) {
          defaultCountryCode = currencyRecords[0].code;
        }
      }
      setCurrencyCode(defaultCountryCode);
    } else {
      setPlantId('');
      setPlant('');
      setCurrencyCode('USD');
    }

    if (selectedFramework && selectedPlant) {
      const commonTechnologies = selectedFramework.technologies?.filter((frameworkTechs) =>
        selectedPlant.technologies?.some((plantTechs) => frameworkTechs === plantTechs)
      );
      setTechnologies(commonTechnologies);
    } else {
      setTechnologies(undefined);
    }
  };

  const handleTechnologyChange = (event: SelectChangeEvent) => {
    setTechnology(event.target.value);
  };

  const handleCurrencyChange = (event: SelectChangeEvent) => {
    setCurrencyCode(event.target.value as CurrencyCode);
  };

  const validateForm = () => {
    if (startYear > endYear || (startYear === endYear && startMonth > endMonth)) {
      enqueueSnackbar('Project end date cannot be less than start date.', { variant: 'error' });
      return false;
    }

    return true;
  };

  const saveData = async (): Promise<void> => {
    setIsSaving(true);

    try {
      if (validateForm()) {
        await onSubmit({
          ...project,
          id: project?.id ?? '',
          name,
          plant,
          plantId,
          framework,
          frameworkId,
          technology,
          managers,
          currencyCode,
          startMonth,
          startYear,
          endMonth,
          endYear,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title="Project" />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  value={name}
                  label="Name"
                  name="name"
                  disabled={isEditMode()}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Framework</InputLabel>
                <Select
                  label="Framework"
                  id="framework-select"
                  value={frameworkId}
                  onChange={handleFrameworkChange}
                  onOpen={() => setFrameworksLoadingTriggered(true)}
                  sx={{ color: 'inherit' }}
                  disabled={isEditMode()}
                >
                  {(
                    frameworks?.sort((one, two) => (one.name < two.name ? -1 : 1)) ?? [
                      { id: frameworkId, name: framework },
                    ]
                  ).map((framework) => (
                    <MenuItem key={framework.id} value={framework.id}>
                      {framework.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Plant</InputLabel>
                <Select
                  label="Plant"
                  id="plant-select"
                  variant="outlined"
                  value={plantId}
                  onChange={handlePlantChange}
                  onOpen={() => setPlantsLoadingTriggered(true)}
                  sx={{ color: 'inherit' }}
                  disabled={isEditMode()}
                >
                  {(plants?.sort((one, two) => (one.name < two.name ? -1 : 1)) ?? [{ id: plantId, name: plant }]).map(
                    (plant) => (
                      <MenuItem key={plant.id} value={plant.id}>
                        {plant.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Technology</InputLabel>
                <Select
                  label="Technology"
                  id="technology-select"
                  variant="outlined"
                  value={technology}
                  onChange={handleTechnologyChange}
                  sx={{ color: 'inherit' }}
                  disabled={isEditMode()}
                >
                  {(technologies?.sort((one, two) => (one < two ? -1 : 1)) ?? [technology]).map((technology) => (
                    <MenuItem key={technology} value={technology}>
                      {technology}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  id="currency-select"
                  variant="outlined"
                  value={currencyCode}
                  onChange={handleCurrencyChange}
                  sx={{ color: 'inherit' }}
                  disabled={isEditMode()}
                >
                  {_default.data?.map((currencyRecord) => (
                    <MenuItem key={currencyRecord.code} value={currencyRecord.code}>
                      {`${currencyRecord.code} (${currencyRecord.currency})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid md={3} xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Start month</InputLabel>
                <OutlinedInput
                  type="number"
                  inputMode="numeric"
                  inputProps={{ min: 1, max: 12 }}
                  onChange={(e) => {
                    setStartMonth(+e.target.value);
                  }}
                  value={startMonth}
                  label="Start month"
                  name="startMonth"
                  disabled={isEditMode()}
                />
              </FormControl>
            </Grid>
            <Grid md={3} xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Start year</InputLabel>
                <OutlinedInput
                  type="number"
                  inputMode="numeric"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  onChange={(e) => {
                    setStartYear(+e.target.value);
                  }}
                  value={startYear}
                  label="Start year"
                  name="startYear"
                  disabled={isEditMode()}
                />
              </FormControl>
            </Grid>
            <Grid md={3} xs={6}>
              <FormControl fullWidth required>
                <InputLabel>End month</InputLabel>
                <OutlinedInput
                  type="number"
                  inputMode="numeric"
                  inputProps={{ min: 1, max: 12 }}
                  onChange={(e) => {
                    setEndMonth(+e.target.value);
                  }}
                  value={endMonth}
                  label="End month"
                  name="endMonth"
                  disabled={isEditMode()}
                />
              </FormControl>
            </Grid>
            <Grid md={3} xs={6}>
              <FormControl fullWidth required>
                <InputLabel>End year</InputLabel>
                <OutlinedInput
                  type="number"
                  inputMode="numeric"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  onChange={(e) => {
                    setEndYear(+e.target.value);
                  }}
                  value={endYear}
                  label="End year"
                  name="endYear"
                  disabled={isEditMode()}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <UserSelect
                  id="managers-filled"
                  organisationId={organisationId}
                  label="Managers"
                  value={managers}
                  onChange={setManagers}
                  disabled={isEditMode() && !PermissionsHelper.hasUserAtLeastRole('plantManager', user, project)}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isSaving || (isEditMode() && !PermissionsHelper.hasUserAtLeastRole('plantManager', user, project))
            }
          >
            Save details
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
